import { useState, useEffect } from 'react';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/feature/SEOHead';

interface Award {
  id: number;
  award_title: string;
  award_description: string;
  award_year: string;
  award_organization: string;
  sort_order: number;
}

interface AwardsPageSettings {
  awards_enabled: string;
  awards_title: string;
  awards_subtitle: string;
  awards_media_type: string;
  awards_media_url: string;
  awards_title_font?: string;
  awards_subtitle_font?: string;
}

interface CustomFont {
  font_family: string;
  font_display_name: string;
  woff2_url?: string;
  woff_url?: string;
  ttf_url?: string;
  otf_url?: string;
}

export default function Awards() {
  const [awards, setAwards] = useState<Award[]>([]);
  const [settings, setSettings] = useState<AwardsPageSettings>({
    awards_enabled: '1',
    awards_title: 'Awards & Recognition',
    awards_subtitle: 'Celebrating our creative achievements and industry recognition over the years.',
    awards_media_type: 'image',
    awards_media_url: '',
    awards_title_font: 'Inter',
    awards_subtitle_font: 'Inter'
  });
  const [customFonts, setCustomFonts] = useState<CustomFont[]>([]);
  const [loading, setLoading] = useState(true);
  const [fontsLoaded, setFontsLoaded] = useState(false);

  // Helper function to render text with line breaks
  const renderTextWithBreaks = (text: string) => {
    return text.split('<br>').map((line, index, array) => (
      <span key={index}>
        {line}
        {index < array.length - 1 && <br />}
      </span>
    ));
  };

  useEffect(() => {
    // Fetch awards page settings
    fetch('/admin/content-api.php')
      .then(response => response.json())
      .then(data => {
        if (data.success && data.data) {
          setSettings(prevSettings => ({
            ...prevSettings,
            ...data.data
          }));
          
          // Fetch custom fonts if available
          if (data.custom_fonts) {
            setCustomFonts(data.custom_fonts);
          }
        }
      })
      .catch(error => {
        console.log('Could not fetch page settings:', error);
      });

    // Fetch awards data
    fetch('/admin/api.php?action=get_awards')
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          setAwards(data.data);
        }
        setLoading(false);
      })
      .catch(error => {
        console.log('Could not fetch awards:', error);
        setLoading(false);
      });
  }, []);

  // Load custom fonts and Google Fonts with Safari compatibility
  useEffect(() => {
    const loadFonts = async () => {
      // Load custom fonts
      if (customFonts.length > 0) {
        const style = document.createElement('style');
        let cssRules = '';
        
        customFonts.forEach(font => {
          const sources = [];
          if (font.woff2_url) sources.push(`url('${font.woff2_url}') format('woff2')`);
          if (font.woff_url) sources.push(`url('${font.woff_url}') format('woff')`);
          if (font.ttf_url) sources.push(`url('${font.ttf_url}') format('truetype')`);
          if (font.otf_url) sources.push(`url('${font.otf_url}') format('opentype')`);
          
          if (sources.length > 0) {
            cssRules += `
              @font-face {
                font-family: '${font.font_family}';
                src: ${sources.join(', ')};
                font-display: swap;
                font-weight: normal;
                font-style: normal;
              }
            `;
          }
        });
        
        if (cssRules) {
          style.textContent = cssRules;
          document.head.appendChild(style);
        }
      }

      // Load Google Fonts for awards page fonts
      const googleFontsToLoad = new Set<string>();
      
      if (settings.awards_title_font && settings.awards_title_font !== 'Inter' && !customFonts.find(f => f.font_family === settings.awards_title_font)) {
        googleFontsToLoad.add(settings.awards_title_font);
      }
      
      if (settings.awards_subtitle_font && settings.awards_subtitle_font !== 'Inter' && !customFonts.find(f => f.font_family === settings.awards_subtitle_font)) {
        googleFontsToLoad.add(settings.awards_subtitle_font);
      }

      // Load Google Fonts with better Safari compatibility
      const loadGoogleFont = (fontName: string) => {
        return new Promise<void>((resolve) => {
          const fontNameForUrl = fontName.replace(/\s+/g, '+');
          const existingLink = document.querySelector(`link[href*="${fontNameForUrl}"]`);
          
          if (!existingLink) {
            const link = document.createElement('link');
            link.href = `https://fonts.googleapis.com/css2?family=${fontNameForUrl}:wght@400;500;600;700;800;900&display=swap`;
            link.rel = 'stylesheet';
            link.crossOrigin = 'anonymous';
            
            // Add load event listener for Safari compatibility
            link.onload = () => {
              console.log(`Awards page font loaded: ${fontName}`);
              resolve();
            };
            
            link.onerror = () => {
              console.warn(`Failed to load awards page font: ${fontName}`);
              resolve();
            };
            
            document.head.appendChild(link);
            
            // Fallback timeout for Safari
            setTimeout(() => {
              resolve();
            }, 3000);
          } else {
            resolve();
          }
        });
      };

      // Load all Google Fonts
      const fontPromises = Array.from(googleFontsToLoad).map(fontName => loadGoogleFont(fontName));
      
      try {
        await Promise.all(fontPromises);
        
        // Force font loading check for Safari
        if (googleFontsToLoad.size > 0) {
          // Use document.fonts API if available (modern browsers including Safari)
          if ('fonts' in document) {
            try {
              await document.fonts.ready;
              console.log('All awards page fonts loaded successfully');
            } catch (error) {
              console.warn('Awards page font loading check failed:', error);
            }
          }
        }
        
        setFontsLoaded(true);
      } catch (error) {
        console.warn('Error loading awards page fonts:', error);
        setFontsLoaded(true);
      }
    };

    if (!loading && Object.keys(settings).length > 0) {
      loadFonts();
    }
  }, [settings, customFonts, loading]);

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Awards & Recognition - Roel & Wietse Creative Team",
    "description": "Celebrating our creative achievements and industry recognition over the years in art direction and copywriting.",
    "url": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/awards`,
    "mainEntity": {
      "@type": "Organization",
      "name": "Roel & Wietse Creative Team",
      "award": awards.map(award => ({
        "@type": "Award",
        "name": award.award_title,
        "description": award.award_description,
        "dateReceived": award.award_year,
        "awardingOrganization": {
          "@type": "Organization",
          "name": award.award_organization
        }
      }))
    }
  };

  // If awards page is disabled, show 404
  if (!loading && settings.awards_enabled !== '1') {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-6xl font-bold text-gray-300 mb-4">404</h1>
          <p className="text-gray-600 mb-8">This page is currently unavailable.</p>
          <a 
            href="/" 
            className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
          >
            Return Home
          </a>
        </div>
      </div>
    );
  }

  if (loading || !fontsLoaded) {
    return (
      <div className="min-h-screen bg-white">
        <Navigation />
        <div className="flex items-center justify-center py-32">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto mb-4"></div>
            <p className="text-gray-600">Loading awards...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Dynamic styles for typography with Safari font fallback
  const titleStyle = {
    fontFamily: settings.awards_title_font ? `"${settings.awards_title_font}", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif` : undefined
  };

  const subtitleStyle = {
    fontFamily: settings.awards_subtitle_font ? `"${settings.awards_subtitle_font}", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif` : undefined
  };

  const renderMedia = () => {
    if (!settings.awards_media_url) {
      return (
        <div className="relative w-full h-96 rounded-2xl overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=awards%20ceremony%20golden%20trophies%20elegant%20professional%20photography%2C%20creative%20industry%20awards%20display%2C%20minimalist%20modern%20awards%20showcase%2C%20professional%20recognition%20ceremony%2C%20golden%20awards%20on%20sleek%20podium%20with%20dramatic%20lighting%20and%20sophisticated%20background&width=1200&height=600&seq=21&orientation=landscape"
            alt="Awards"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-black bg-opacity-20"></div>
        </div>
      );
    }

    if (settings.awards_media_type === 'video') {
      return (
        <div className="relative w-full h-96 rounded-2xl overflow-hidden">
          <video
            className="w-full h-full object-cover"
            autoPlay
            muted
            loop
            playsInline
          >
            <source src={settings.awards_media_url} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
          <div className="absolute inset-0 bg-black bg-opacity-30"></div>
        </div>
      );
    } else {
      return (
        <div className="relative w-full h-96 rounded-2xl overflow-hidden">
          <img
            src={settings.awards_media_url}
            alt="Awards"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-black bg-opacity-20"></div>
        </div>
      );
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Awards & Recognition - Roel & Wietse Creative Team"
        description="Celebrating our creative achievements and industry recognition over the years in art direction and copywriting."
        keywords="Awards, Recognition, Creative awards, Industry recognition, Art direction awards, Copywriting awards"
        schemaData={schemaData}
      />
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center bg-white overflow-hidden">
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-20">
          <div className="text-center max-w-4xl mx-auto">
            <h1 
              className={`${content.awards_hero_title_size || 'text-5xl md:text-6xl'} font-bold text-gray-900 mb-6 leading-tight`}
              style={heroTitleStyle}
            >
              {renderTextWithBreaks(content.awards_hero_title || 'Recognition & Awards')}
            </h1>
            <p 
              className={`${content.awards_hero_subtitle_size || 'text-xl'} text-gray-600 leading-relaxed`}
              style={heroSubtitleStyle}
            >
              {renderTextWithBreaks(content.awards_hero_subtitle || 'Our work has been recognized by leading industry organizations and creative communities worldwide.')}
            </p>
          </div>
        </div>
      </section>

      {/* Awards List */}
      <section className="py-20 px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          {awards.length === 0 ? (
            <div className="text-center py-20">
              <i className="ri-award-line text-6xl text-gray-300 mb-6"></i>
              <h3 className="text-2xl font-semibold text-gray-400 mb-4">No Awards Added Yet</h3>
              <p className="text-gray-500">Check back soon for our latest achievements and recognition.</p>
            </div>
          ) : (
            <div className="space-y-8">
              {awards.map((award, index) => (
                <div 
                  key={award.id}
                  className="border-b border-gray-100 pb-8 last:border-b-0 last:pb-0"
                >
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
                    <div className="flex-1">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <i className="ri-award-fill text-yellow-600 text-xl"></i>
                        </div>
                        <div>
                          <h3 className="text-2xl font-bold text-gray-900 leading-tight">
                            {award.award_title}
                          </h3>
                          {award.award_organization && (
                            <p className="text-gray-600 font-medium mt-1">
                              <i className="ri-building-line mr-2"></i>
                              {award.award_organization}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      {award.award_description && (
                        <p className="text-gray-700 text-lg leading-relaxed ml-16">
                          {award.award_description}
                        </p>
                      )}
                    </div>
                    
                    {award.award_year && (
                      <div className="flex-shrink-0 md:ml-6">
                        <span className="inline-block px-4 py-2 bg-black text-white text-lg font-semibold rounded-full">
                          {award.award_year}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
